/**
 * PageAlternatesContext
 *
 * Allows individual pages (e.g. BlogDetail) to register alternate URLs for
 * each language. LanguageSwitcher reads these and navigates directly to the
 * correct URL instead of doing a generic slug translation.
 *
 * Usage:
 *   // In BlogDetail:
 *   const { setAlternates } = usePageAlternates();
 *   useEffect(() => {
 *     setAlternates({ en: "https://...", fr: "https://..." });
 *     return () => setAlternates({});
 *   }, [article]);
 *
 *   // In LanguageSwitcher:
 *   const { alternates } = usePageAlternates();
 *   // Use alternates[targetLang] if available, else fall back to normal routing.
 */

import { createContext, useContext, useState, type ReactNode } from "react";

type Alternates = Record<string, string>; // lang code → full URL

interface PageAlternatesContextValue {
  alternates: Alternates;
  setAlternates: (a: Alternates) => void;
}

const PageAlternatesContext = createContext<PageAlternatesContextValue>({
  alternates: {},
  setAlternates: () => {},
});

export function PageAlternatesProvider({ children }: { children: ReactNode }) {
  const [alternates, setAlternates] = useState<Alternates>({});
  return (
    <PageAlternatesContext.Provider value={{ alternates, setAlternates }}>
      {children}
    </PageAlternatesContext.Provider>
  );
}

export function usePageAlternates() {
  return useContext(PageAlternatesContext);
}
